import { CloseOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { Drawer, Spin, Tabs } from 'antd';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { OrderDetail } from '../OrderDetail';
import { useTranslate } from '@tolgee/react';
import { OrderQueryParams } from 'hooks/business/chat/useOrderList';
import { OrderCancelDetail } from '../OrderCancelDetail';
import { useOrderReturn } from 'hooks/business/chat/useOrderReturn';
import { OrderReturnDetail } from '../OrderReturnDetail';
import { OrderDrawerEntryParams, OrderDrawerTab } from 'components/LightConversation/types';
import { queries } from 'services/queries';

interface OrderDrawerProps extends OrderDrawerEntryParams {
  conversation: Conversation.Conversation;
  visible: boolean;
  order?: Conversation.Trade.Vo;
  sendable: boolean;
  /** 用于乐观更新的查询参数 */
  orderQueryParams?: OrderQueryParams;
  onClose: () => void;
  defaultActiveTab?: OrderDrawerTab;
  contactEnable?: boolean;
}

type OrderDrawerInnerProps = Omit<OrderDrawerProps, 'order'> & {
  order: Conversation.Trade.Vo;
  contactEnable?: boolean;
};

const OrderDrawerInner = (props: OrderDrawerInnerProps) => {
  const {
    order,
    conversation,
    sendable,
    orderQueryParams,
    defaultActiveTab,
    entryType = 'order',
    contactEnable = false,
  } = props;
  const { t } = useTranslate();
  const initialTab = useMemo<OrderDrawerTab>(() => {
    if (defaultActiveTab) return defaultActiveTab;
    if (entryType === 'refund') return 'refundDetail';
    if (entryType === 'cancel') return 'cancelDetail';
    return 'orderDetail';
  }, [defaultActiveTab, entryType]);
  const [activeTab, setActiveTab] = useState<OrderDrawerTab>(initialTab);
  const cancelTrade = order.cancelTrade || [];

  const { returnSnList } = useOrderReturn(order);

  const isCancelDetail = cancelTrade?.length > 0;
  const isRefundDetail = returnSnList?.length > 0;

  const showTabs = isCancelDetail || isRefundDetail;

  return (
    <>
      {showTabs && (
        <Tabs
          className={tabsCls}
          activeKey={activeTab}
          onChange={(key) => {
            const nextTab = key as OrderDrawerTab;
            setActiveTab(nextTab);
          }}
        >
          <Tabs.TabPane tab={t('trade.订单详情')} key="orderDetail" />
          {isCancelDetail && <Tabs.TabPane tab={t('trade.取消订单详情')} key="cancelDetail" />}
          {(isRefundDetail || entryType === 'refund') && (
            <Tabs.TabPane tab={t('sidebar.order.detail.returnRefund.title')} key="refundDetail" />
          )}
        </Tabs>
      )}
      {activeTab === 'orderDetail' && (
        <OrderDetail
          conversation={conversation}
          order={order}
          sendable={sendable}
          queryParams={orderQueryParams}
        />
      )}
      {activeTab === 'cancelDetail' && (
        <OrderCancelDetail
          order={order}
          orderQueryParams={orderQueryParams}
          contactEnable={contactEnable}
        />
      )}
      {activeTab === 'refundDetail' && (
        <OrderReturnDetail
          order={order}
          orderQueryParams={orderQueryParams}
          contactEnable={contactEnable}
        />
      )}
    </>
  );
};

export const OrderDrawer = (props: OrderDrawerProps) => {
  const { order, entryType = 'order', returnOrder, cancelOrder, visible, onClose } = props;
  const { t } = useTranslate();
  const shouldFetchDetail = !order;

  const params = useMemo(() => {
    if (returnOrder) {
      return {
        externalTradeId: returnOrder.orderSn,
        storeId: returnOrder.internalStoreId,
        buyerId: returnOrder.externalBuyerId,
      };
    }
    if (cancelOrder) {
      return {
        externalTradeId: cancelOrder.orderId,
        storeId: cancelOrder.storeId,
        buyerId: cancelOrder.buyerId,
      };
    }
    return null;
  }, [returnOrder, cancelOrder]);

  const { data: detailResp } = useQuery({
    ...queries.orders.detail(params!),
    enabled: shouldFetchDetail && !!params,
  });

  const remoteOrder = detailResp?.tradeList?.[0];

  const finalOrder = order || remoteOrder;

  const titleId = finalOrder?.externalTradeId || returnOrder?.orderSn || cancelOrder?.orderId;
  const containsPreOrderItem =
    finalOrder?.containsPreOrderItem || returnOrder?.containsPreOrderItem;

  return (
    <Drawer
      destroyOnClose
      width={1000}
      visible={visible}
      bodyStyle={{
        background: '#F7F9FA',
        padding: 0,
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
      headerStyle={{ background: '#F7F9FA', border: 'none', padding: '16px' }}
      onClose={onClose}
      closable={false}
      title={
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="bg-transparent border-none bg-[#F0F2F5] p-1 flex items-center justify-center rounded cursor-pointer hover:bg-[#E1E4EB]"
            onClick={onClose}
          >
            <CloseOutlined className="text-[#515359] text-[16px]" />
          </button>
          <div className="text-[20px] leading-[18px] font-medium text-[#1A1B1C] flex items-center gap-1">
            {titleId}
            {containsPreOrderItem && (
              <span className="inline-block text-[#00BF69] bg-[#EBFAF3] text-[12px] leading-[22px] px-1 rounded-sm">
                {t('trade.预购')}
              </span>
            )}
          </div>
        </div>
      }
      footer={null}
    >
      {!finalOrder && shouldFetchDetail && (
        <div className="flex-1 flex items-center justify-center">
          <Spin />
        </div>
      )}
      {finalOrder && <OrderDrawerInner {...props} order={finalOrder} entryType={entryType} />}
    </Drawer>
  );
};

const tabsCls = css`
  flex-shrink: 0;
  overflow: hidden;
  margin-bottom: 16px;

  .ant-tabs-nav {
    margin: 0 16px 0 16px;
    padding: 0 16px;
    background: #ffffff;
  }

  .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    color: #232426;
    font-weight: 500;
  }
  .ant-tabs-tab:hover,
  .ant-tabs-tab-btn:active,
  .ant-tabs-tab-btn:focus,
  .ant-tabs-tab-remove:active,
  .ant-tabs-tab-remove:focus {
    color: #232426 !important;
  }
`;
